/**
 * Index Channel Page - Parse and import index channel entries
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
} from '@mui/material';
import { useManuscriptChats } from '@/hooks/useMessages';
import { useChatLabel } from '@/hooks/useChatLabel';
import { useLibraries } from '@/hooks/useLibraries';
import { useParseIndexChannel, useImportIndexChannel } from '@/hooks/useIndexChannel';
import { ParsedIndexEntry, LibraryMatchResult, LibraryDecision } from '@/types/indexChannel';
import IndexChannelSelector from '@/components/indexChannel/IndexChannelSelector';
import IndexEntriesList from '@/components/indexChannel/IndexEntriesList';
import ContextPanel from '@/components/messages/ContextPanel';

const IndexChannelPage: React.FC = () => {
  const { t } = useTranslation(['pages', 'review']);
  const chatLabel = useChatLabel();

  const [targetChatId, setTargetChatId] = useState<number | null>(null);
  const [indexChannelId, setIndexChannelId] = useState<number | null>(null);
  const [indexStartMessageId, setIndexStartMessageId] = useState<string>('');
  const [indexEndMessageId, setIndexEndMessageId] = useState<string>('');
  const [indexEntries, setIndexEntries] = useState<ParsedIndexEntry[]>([]);
  const [libraryMatches, setLibraryMatches] = useState<LibraryMatchResult[]>([]);
  const [selectedEntries, setSelectedEntries] = useState<Set<string>>(new Set());
  const [libraryDecisions, setLibraryDecisions] = useState<Map<string, LibraryDecision>>(new Map());

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const [contextPanelOpen, setContextPanelOpen] = useState(false);
  const [contextMessageId, setContextMessageId] = useState<number | null>(null);
  const [contextChatId, setContextChatId] = useState<number | null>(null);

  const { data: chats = [], isLoading: chatsLoading } = useManuscriptChats();
  const { data: libraries = [] } = useLibraries();
  const parseIndexMutation = useParseIndexChannel();
  const importIndexMutation = useImportIndexChannel();

  useEffect(() => {
    if (targetChatId === null && chats.length > 0) {
      setTargetChatId(chats[0].id);
    }
  }, [targetChatId, chats]);

  const handleIndexChannelSelect = (chatId: number | null) => {
    setIndexChannelId(chatId);
    setIndexEntries([]);
    setLibraryMatches([]);
    setSelectedEntries(new Set());
    setLibraryDecisions(new Map());
  };

  const parseOptionalInt = (value: string): number | null => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number.parseInt(trimmed, 10);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const handleParseIndex = async () => {
    if (!indexChannelId || !targetChatId) {
      alert(t('review:indexChannel.selectChats'));
      return;
    }

    const indexStartInput = parseOptionalInt(indexStartMessageId);
    const indexEndInput = parseOptionalInt(indexEndMessageId);
    const indexStart = indexStartInput ?? 1;
    const indexEnd = indexEndInput ?? undefined;

    if ((indexStartInput !== null && indexStartInput <= 0) || indexEndInput === null && indexEndMessageId.trim()) {
      alert(t('review:generate.errors.invalidMessageIds'));
      return;
    }
    if (indexEndInput !== null && indexEndInput < indexStart) {
      alert(t('review:generate.errors.startAfterEnd'));
      return;
    }

    try {
      const result = await parseIndexMutation.mutateAsync({
        index_chat_id: indexChannelId,
        start_message_id: indexStart,
        end_message_id: indexEnd,
      });

      const sortedEntries = [...result.entries].sort((a, b) =>
        (a.target_message_id || 0) - (b.target_message_id || 0)
      );

      const entriesWithEndIds = sortedEntries.map((entry, index) => {
        const nextEntry = sortedEntries[index + 1];
        const calculatedEnd = nextEntry?.target_message_id
          ? nextEntry.target_message_id - 1
          : (indexEnd ?? nextEntry?.target_message_id ?? undefined);

        return {
          ...entry,
          calculated_end_message_id: calculatedEnd,
        };
      });

      setIndexEntries(entriesWithEndIds);
      setLibraryMatches(result.library_matches);

      const autoSelected = new Set<string>();
      const decisions = new Map<string, LibraryDecision>();

      result.entries.forEach((entry) => {
        const entryId = `${entry.index_no}-${entry.library_hashtag}`;
        const match = result.library_matches.find(
          (m) => m.hashtag === entry.library_hashtag
        );

        if (match?.matched_library_id) {
          autoSelected.add(entryId);
          decisions.set(entryId, {
            action: 'link',
            library_id: match.matched_library_id,
          });
        }
      });

      setSelectedEntries(autoSelected);
      setLibraryDecisions(decisions);

      setSnackbarMessage(
        t('review:indexChannel.foundAndMatched', {
          entries: result.entries_found,
          matched: result.matched_count,
          unmatched: result.unmatched_count,
        })
      );
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Failed to parse index channel:', error);
    }
  };

  const handleToggleEntry = (entryId: string) => {
    setSelectedEntries((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(entryId)) {
        newSet.delete(entryId);
      } else {
        newSet.add(entryId);
      }
      return newSet;
    });
  };

  const handleUpdateRange = (entryId: string, start: number, end: number) => {
    setIndexEntries((prev) =>
      prev.map((entry) => {
        const currentId = `${entry.index_no}-${entry.library_hashtag}`;
        if (currentId === entryId) {
          return {
            ...entry,
            target_message_id: start,
            calculated_end_message_id: end,
          };
        }
        return entry;
      })
    );
  };

  const handleDecisionChange = (entryId: string, decision: LibraryDecision) => {
    setLibraryDecisions((prev) => {
      const newMap = new Map(prev);
      if (decision.action === 'skip') {
        newMap.delete(entryId);
        setSelectedEntries((prevSet) => {
          const newSet = new Set(prevSet);
          newSet.delete(entryId);
          return newSet;
        });
      } else {
        newMap.set(entryId, decision);
        setSelectedEntries((prevSet) => new Set(prevSet).add(entryId));
      }
      return newMap;
    });
  };

  const handleImportSections = async () => {
    if (!indexChannelId || !targetChatId || selectedEntries.size === 0) {
      alert(t('review:indexChannel.noEntriesToImport'));
      return;
    }

    const indexStartInput = parseOptionalInt(indexStartMessageId);
    const indexEndInput = parseOptionalInt(indexEndMessageId);
    const indexStart = indexStartInput ?? 1;
    const indexEnd = indexEndInput ?? undefined;

    if ((indexStartInput !== null && indexStartInput <= 0) || indexEndInput === null && indexEndMessageId.trim()) {
      alert(t('review:generate.errors.invalidMessageIds'));
      return;
    }
    if (indexEndInput !== null && indexEndInput < indexStart) {
      alert(t('review:generate.errors.startAfterEnd'));
      return;
    }

    const decisionsToImport = Array.from(selectedEntries)
      .map((entryId) => {
        const decision = libraryDecisions.get(entryId);
        if (!decision || decision.action === 'skip') return null;

        const entry = indexEntries.find(
          (e) => `${e.index_no}-${e.library_hashtag}` === entryId
        );
        if (!entry) return null;

        const importDecision: {
          hashtag: string;
          action: 'create' | 'link';
          library_id?: number;
          canonical_name?: string;
        } = {
          hashtag: entry.library_hashtag || '',
          action: decision.action as 'create' | 'link',
        };

        if (decision.action === 'link') {
          importDecision.library_id = decision.library_id;
        } else if (decision.action === 'create') {
          importDecision.canonical_name = decision.canonical_name;
        }

        return importDecision;
      })
      .filter((d) => d !== null) as {
      hashtag: string;
      action: 'create' | 'link';
      library_id?: number;
      canonical_name?: string;
    }[];

    if (decisionsToImport.length === 0) {
      alert(t('review:indexChannel.noEntriesToImport'));
      return;
    }

    try {
      const result = await importIndexMutation.mutateAsync({
        index_chat_id: indexChannelId,
        target_chat_id: targetChatId,
        start_message_id: indexStart,
        end_message_id: indexEnd,
        library_decisions: decisionsToImport,
      });

      setSnackbarMessage(
        t('review:indexChannel.importSuccess', {
          sections: result.sections_created,
          newLibs: result.libraries_created,
          linkedLibs: result.libraries_linked,
        })
      );
      setSnackbarOpen(true);

      setSelectedEntries(new Set());
    } catch (error) {
      console.error('Failed to import index channel:', error);
      setSnackbarMessage(t('review:indexChannel.importFailed'));
      setSnackbarOpen(true);
    }
  };

  const handleSkipAll = () => {
    setIndexEntries([]);
    setLibraryMatches([]);
    setSelectedEntries(new Set());
    setLibraryDecisions(new Map());
  };

  const handleViewMessage = useCallback((messageId: number, msgChatId: number) => {
    setContextMessageId(messageId);
    setContextChatId(msgChatId);
    setContextPanelOpen(true);
  }, []);

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <Box
        sx={{
          flex: contextPanelOpen ? '0 0 calc(100% - 420px)' : 1,
          overflowY: 'auto',
          p: 3,
          transition: 'flex 0.3s ease',
        }}
      >
        <Typography variant="h4" gutterBottom>
          {t('pages:indexChannel.title')}
        </Typography>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('pages:indexChannel.setupTitle')}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth disabled={chatsLoading || chats.length === 0}>
                <InputLabel>{t('pages:indexChannel.targetChatLabel')}</InputLabel>
                <Select
                  value={targetChatId ?? ''}
                  onChange={(e) => setTargetChatId(Number(e.target.value))}
                  label={t('pages:indexChannel.targetChatLabel')}
                  displayEmpty
                >
                  {chats.map((chat) => (
                    <MenuItem key={chat.id} value={chat.id}>
                      {chatLabel(chat)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <IndexChannelSelector
                chats={chats}
                selectedIndexChannelId={indexChannelId}
                onSelect={handleIndexChannelSelect}
                onParse={handleParseIndex}
                isParsing={parseIndexMutation.isPending}
                isDisabled={chatsLoading || !targetChatId}
                entriesCount={indexEntries.length}
                indexStartMessageId={indexStartMessageId}
                indexEndMessageId={indexEndMessageId}
                onIndexStartChange={setIndexStartMessageId}
                onIndexEndChange={setIndexEndMessageId}
              />
            </Grid>
          </Grid>
        </Paper>

        {indexEntries.length > 0 && (
          <IndexEntriesList
            entries={indexEntries}
            libraryMatches={libraryMatches}
            libraries={libraries}
            selectedEntries={selectedEntries}
            decisions={libraryDecisions}
            onToggleEntry={handleToggleEntry}
            onUpdateRange={handleUpdateRange}
            onDecisionChange={handleDecisionChange}
            onImport={handleImportSections}
            onSkipAll={handleSkipAll}
            onViewMessage={handleViewMessage}
            isImporting={importIndexMutation.isPending}
            stats={{
              total_entries: indexEntries.length,
              matched: libraryMatches.filter((m) => m.matched_library_id !== null).length,
              unmatched: libraryMatches.filter((m) => m.matched_library_id === null).length,
              total_manuscripts: indexEntries.reduce(
                (sum, e) => sum + (e.manuscript_count || 0),
                0
              ),
            }}
          />
        )}

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </Box>

      {contextPanelOpen && (
        <ContextPanel
          open={contextPanelOpen}
          chatId={contextChatId ?? targetChatId}
          anchorId={contextMessageId}
          suggestions={[]}
          onClose={() => setContextPanelOpen(false)}
          onJumpToId={(messageId) => {
            const targetId = contextChatId ?? targetChatId;
            if (targetId) {
              handleViewMessage(messageId, targetId);
            }
          }}
        />
      )}
    </Box>
  );
};

export default IndexChannelPage;
